import React, { useState, useEffect, useMemo } from 'react'
import { supabase } from './supabaseClient'
import { useAuth } from './AuthContext'
import { C } from './theme'
import {
  History, Search, Download, RefreshCw, X, Filter, Package,
  TrendingUp, TrendingDown, AlertTriangle, Sparkles,
} from 'lucide-react'

function fmt(n) {
  const v = Number(n || 0)
  return v.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtDate(iso) {
  if (!iso) return '-'
  const d = new Date(iso)
  return d.toLocaleDateString('pt-PT')
}

function fmtDateTime(iso) {
  if (!iso) return '-'
  const d = new Date(iso)
  return d.toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function csvEscape(v) {
  const s = String(v ?? '')
  if (s.includes(';') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"'
  }
  return s
}

export default function HistoricoPrecos() {
  const { isAdmin } = useAuth()

  const [loading, setLoading] = useState(true)
  const [errorMsg, setErrorMsg] = useState('')
  const [linhas, setLinhas] = useState([])

  const [filtros, setFiltros] = useState(() => {
    const hoje = new Date()
    const noventa = new Date(); noventa.setDate(noventa.getDate() - 90)
    return {
      cliente: '',
      dataDe: noventa.toISOString().slice(0, 10),
      dataAte: hoje.toISOString().slice(0, 10),
      factura: '',
      produto: '',
      motivo: '',
      modo: '',
    }
  })

  const carregar = async () => {
    setLoading(true)
    setErrorMsg('')
    try {
      const { data, error } = await supabase.from('historico_precos_alteracoes')
        .select('*')
        .order('created_at', { ascending: false })
      if (error) throw error
      setLinhas(data || [])
    } catch (e) {
      setErrorMsg(e?.message || String(e))
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { carregar() }, [])

  const clientesDropdown = useMemo(() => {
    const set = new Set()
    for (const l of linhas) if (l.cliente_email) set.add(l.cliente_email)
    return Array.from(set).sort()
  }, [linhas])

  const filtradas = useMemo(() => {
    const de = filtros.dataDe
    const ate = filtros.dataAte
    const facturaF = (filtros.factura || '').trim().toLowerCase()
    const prodF = (filtros.produto || '').trim().toLowerCase()
    return linhas.filter(l => {
      if (filtros.cliente && l.cliente_email !== filtros.cliente) return false
      const d = String(l.created_at || '').slice(0, 10)
      if (de && d < de) return false
      if (ate && d > ate) return false
      if (facturaF && !(String(l.factura_nova || '').toLowerCase().includes(facturaF) || String(l.factura_antiga || '').toLowerCase().includes(facturaF))) return false
      if (prodF) {
        const nome = String(l.produto_nome || '').toLowerCase()
        const ean = String(l.produto_ean || '').toLowerCase()
        if (!nome.includes(prodF) && !ean.includes(prodF)) return false
      }
      if (filtros.motivo && l.motivo !== filtros.motivo) return false
      if (filtros.modo && l.modo !== filtros.modo) return false
      return true
    })
  }, [linhas, filtros])

  const contadores = useMemo(() => {
    let subida = 0, descida = 0, primeira = 0, manual = 0, semAlt = 0
    let actualizaramVenda = 0
    for (const l of filtradas) {
      if (l.motivo === 'subida') subida++
      else if (l.motivo === 'descida') descida++
      else if (l.motivo === 'primeira_vez') primeira++
      else if (l.motivo === 'manual_aviso') manual++
      else semAlt++
      if (l.actualizado_venda) actualizaramVenda++
    }
    return { subida, descida, primeira, manual, semAlt, actualizaramVenda, total: filtradas.length }
  }, [filtradas])

  const exportarCsv = () => {
    const header = ['DataHora', 'FacturaAntiga', 'FacturaNova', 'Produto', 'EAN', 'Cliente', 'Modo', 'Motivo', 'CustoBrutoAntigo', 'CustoBrutoNovo', 'CustoRealAntigo', 'CustoRealNovo', 'DeltaCusto', 'VendaAntiga', 'VendaNova', 'DeltaVenda', 'ActualizadoVenda']
    const rows = filtradas.map(l => [
      fmtDateTime(l.created_at),
      l.factura_antiga || '',
      l.factura_nova || '',
      l.produto_nome,
      l.produto_ean || '',
      l.cliente_email,
      l.modo,
      l.motivo,
      l.preco_fornecedor_antigo != null ? Number(l.preco_fornecedor_antigo).toFixed(2) : '',
      Number(l.preco_fornecedor_novo || 0).toFixed(2),
      l.preco_custo_antigo != null ? Number(l.preco_custo_antigo).toFixed(2) : '',
      Number(l.preco_custo_novo || 0).toFixed(2),
      l.delta_custo != null ? Number(l.delta_custo).toFixed(2) : '',
      l.preco_venda_antigo != null ? Number(l.preco_venda_antigo).toFixed(2) : '',
      l.preco_venda_novo != null ? Number(l.preco_venda_novo).toFixed(2) : '',
      l.delta_venda != null ? Number(l.delta_venda).toFixed(2) : '',
      l.actualizado_venda ? 'sim' : 'nao',
    ].map(csvEscape).join(';'))
    const bom = '﻿'
    const conteudo = bom + [header.join(';'), ...rows].join('\n')
    const blob = new Blob([conteudo], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `historico-precos-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a); a.click(); a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const limparFiltros = () => {
    const hoje = new Date()
    const noventa = new Date(); noventa.setDate(noventa.getDate() - 90)
    setFiltros({
      cliente: '', dataDe: noventa.toISOString().slice(0, 10), dataAte: hoje.toISOString().slice(0, 10),
      factura: '', produto: '', motivo: '', modo: '',
    })
  }

  if (!isAdmin) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
        <div style={{ textAlign: 'center', color: C.textMuted }}>
          <Package size={40} style={{ opacity: 0.4 }} />
          <p style={{ marginTop: '12px', fontSize: '14px' }}>Acesso restrito ao administrador.</p>
        </div>
      </div>
    )
  }

  const inputBase = { padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', background: '#fff', color: C.text, outline: 'none', minHeight: '36px' }
  const labelBase = { fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: '4px' }
  const thBase = { padding: '10px 12px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', background: C.bg, borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1 }
  const tdBase = { padding: '10px 12px', fontSize: '13px', color: C.text, borderBottom: `1px solid ${C.borderLight}`, verticalAlign: 'top' }

  const motivoBadge = (m) => {
    if (m === 'subida') return { txt: '🔺 Subida', bg: '#fde7e9', fg: '#a4262c' }
    if (m === 'descida') return { txt: '🔻 Descida', bg: '#dff6dd', fg: '#107c10' }
    if (m === 'primeira_vez') return { txt: '🆕 Primeira vez', bg: '#eff6fc', fg: '#0078d4' }
    if (m === 'manual_aviso') return { txt: '⚠️ Manual', bg: '#fff4ce', fg: '#c07400' }
    return { txt: m || '—', bg: '#f3f2f1', fg: C.textMuted }
  }

  const modoBadge = (mo) => {
    if (mo === 'auto') return { txt: 'AUTO', bg: '#dff6dd', fg: '#107c10' }
    if (mo === 'manual') return { txt: 'MANUAL', bg: '#fff4ce', fg: '#c07400' }
    if (mo === 'interno') return { txt: 'INTERNO', bg: '#f3f0ff', fg: '#6264a7' }
    return { txt: mo || '—', bg: '#f3f2f1', fg: C.textMuted }
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
      <div style={{ maxWidth: '1500px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          <History size={20} color={C.primary} />
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: C.text, margin: 0 }}>Histórico de Preços</h1>
        </div>
        <p style={{ fontSize: '13px', color: C.textMuted, margin: '0 0 18px' }}>
          Todas as alterações de preços custo/venda registadas ao processar facturas finais do fornecedor. Auditável — usa para confirmar aumentos e reclamar ao fornecedor se necessário.
        </p>

        {/* Cards resumo */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '14px' }}>
          <div style={{ background: '#fde7e9', border: `1px solid #a4262c40`, borderRadius: '6px', padding: '10px 12px' }}>
            <div style={{ fontSize: '10px', color: '#a4262c', textTransform: 'uppercase', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}><TrendingUp size={12} /> Subidas</div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#a4262c', marginTop: '4px' }}>{contadores.subida}</div>
          </div>
          <div style={{ background: '#dff6dd', border: `1px solid #107c1040`, borderRadius: '6px', padding: '10px 12px' }}>
            <div style={{ fontSize: '10px', color: '#107c10', textTransform: 'uppercase', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}><TrendingDown size={12} /> Descidas</div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#107c10', marginTop: '4px' }}>{contadores.descida}</div>
          </div>
          <div style={{ background: '#eff6fc', border: `1px solid #0078d440`, borderRadius: '6px', padding: '10px 12px' }}>
            <div style={{ fontSize: '10px', color: '#0078d4', textTransform: 'uppercase', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}><Sparkles size={12} /> Primeira vez</div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#0078d4', marginTop: '4px' }}>{contadores.primeira}</div>
          </div>
          <div style={{ background: '#fff4ce', border: `1px solid #c0740040`, borderRadius: '6px', padding: '10px 12px' }}>
            <div style={{ fontSize: '10px', color: '#c07400', textTransform: 'uppercase', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}><AlertTriangle size={12} /> Manuais (aviso)</div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: '#c07400', marginTop: '4px' }}>{contadores.manual}</div>
          </div>
          <div style={{ background: '#f3f2f1', border: `1px solid ${C.border}`, borderRadius: '6px', padding: '10px 12px' }}>
            <div style={{ fontSize: '10px', color: C.textMuted, textTransform: 'uppercase', fontWeight: '700' }}>Total (filtrado)</div>
            <div style={{ fontSize: '22px', fontWeight: '800', color: C.text, marginTop: '4px' }}>{contadores.total}</div>
            <div style={{ fontSize: '10px', color: C.textMuted, marginTop: '2px' }}>{contadores.actualizaramVenda} c/ venda actualizada</div>
          </div>
        </div>

        {/* Filtros */}
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '6px', padding: '14px', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Filter size={14} color={C.textMuted} />
            <span style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Filtros</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
            <div>
              <label style={labelBase}>Cliente</label>
              <select value={filtros.cliente} onChange={e => setFiltros(f => ({ ...f, cliente: e.target.value }))} style={{ ...inputBase, width: '100%' }}>
                <option value="">— Todos —</option>
                {clientesDropdown.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={labelBase}>Data de</label>
              <input type="date" value={filtros.dataDe} onChange={e => setFiltros(f => ({ ...f, dataDe: e.target.value }))} style={{ ...inputBase, width: '100%' }} />
            </div>
            <div>
              <label style={labelBase}>Data até</label>
              <input type="date" value={filtros.dataAte} onChange={e => setFiltros(f => ({ ...f, dataAte: e.target.value }))} style={{ ...inputBase, width: '100%' }} />
            </div>
            <div>
              <label style={labelBase}>Factura (ant/nova)</label>
              <input type="text" value={filtros.factura} onChange={e => setFiltros(f => ({ ...f, factura: e.target.value }))} placeholder="76, 84..." style={{ ...inputBase, width: '100%' }} />
            </div>
            <div>
              <label style={labelBase}>Produto/EAN</label>
              <div style={{ position: 'relative' }}>
                <Search size={13} color={C.textMuted} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
                <input type="text" value={filtros.produto} onChange={e => setFiltros(f => ({ ...f, produto: e.target.value }))} placeholder="dior..." style={{ ...inputBase, width: '100%', paddingLeft: '30px' }} />
              </div>
            </div>
            <div>
              <label style={labelBase}>Motivo</label>
              <select value={filtros.motivo} onChange={e => setFiltros(f => ({ ...f, motivo: e.target.value }))} style={{ ...inputBase, width: '100%' }}>
                <option value="">— Todos —</option>
                <option value="subida">🔺 Subida</option>
                <option value="descida">🔻 Descida</option>
                <option value="primeira_vez">🆕 Primeira vez</option>
                <option value="manual_aviso">⚠️ Manual</option>
              </select>
            </div>
            <div>
              <label style={labelBase}>Modo</label>
              <select value={filtros.modo} onChange={e => setFiltros(f => ({ ...f, modo: e.target.value }))} style={{ ...inputBase, width: '100%' }}>
                <option value="">— Todos —</option>
                <option value="auto">AUTO</option>
                <option value="manual">MANUAL</option>
                <option value="interno">INTERNO</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
            <button onClick={limparFiltros} style={{ padding: '7px 12px', border: `1px solid ${C.border}`, background: '#fff', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: C.text, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <X size={12} /> Limpar
            </button>
            <button onClick={carregar} disabled={loading} style={{ padding: '7px 12px', border: `1px solid ${C.border}`, background: '#fff', borderRadius: '4px', cursor: loading ? 'wait' : 'pointer', fontSize: '12px', color: C.text, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <RefreshCw size={12} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} /> Recarregar
            </button>
            <div style={{ flex: 1 }} />
            <button onClick={exportarCsv} disabled={filtradas.length === 0} style={{ padding: '7px 14px', border: 'none', background: C.primary, color: '#fff', borderRadius: '4px', cursor: filtradas.length === 0 ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '5px', opacity: filtradas.length === 0 ? 0.5 : 1 }}>
              <Download size={12} /> Exportar CSV
            </button>
          </div>
        </div>

        {/* Tabela */}
        {errorMsg && (
          <div style={{ padding: '12px', background: '#fef2f2', border: '1px solid #ef4444', borderRadius: '4px', color: '#b91c1c', fontSize: '13px', marginBottom: '10px' }}>
            Erro: {errorMsg}
          </div>
        )}
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: '6px', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', maxHeight: 'calc(100vh - 460px)', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1300px' }}>
              <thead>
                <tr>
                  <th style={thBase}>Data</th>
                  <th style={thBase}>Fact. antiga → nova</th>
                  <th style={thBase}>Produto</th>
                  <th style={thBase}>Cliente</th>
                  <th style={thBase}>Modo</th>
                  <th style={thBase}>Motivo</th>
                  <th style={{ ...thBase, textAlign: 'right' }}>Custo antigo → novo</th>
                  <th style={{ ...thBase, textAlign: 'right' }}>Δ Custo</th>
                  <th style={{ ...thBase, textAlign: 'right' }}>Venda antiga → nova</th>
                  <th style={{ ...thBase, textAlign: 'right' }}>Δ Venda</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} style={{ ...tdBase, textAlign: 'center', color: C.textMuted, padding: '30px' }}>A carregar…</td></tr>
                ) : filtradas.length === 0 ? (
                  <tr><td colSpan={10} style={{ ...tdBase, textAlign: 'center', color: C.textMuted, padding: '30px' }}>Sem alterações registadas para os filtros actuais.</td></tr>
                ) : filtradas.map(l => {
                  const mb = motivoBadge(l.motivo)
                  const md = modoBadge(l.modo)
                  const corDelta = (l.delta_custo || 0) > 0 ? '#a4262c' : (l.delta_custo || 0) < 0 ? '#107c10' : C.textMuted
                  const corDeltaVenda = (l.delta_venda || 0) > 0 ? '#a4262c' : (l.delta_venda || 0) < 0 ? '#107c10' : C.textMuted
                  return (
                    <tr key={l.id}>
                      <td style={{ ...tdBase, whiteSpace: 'nowrap', fontSize: '11px', color: C.textMuted }}>{fmtDateTime(l.created_at)}</td>
                      <td style={{ ...tdBase, fontFamily: 'monospace', fontSize: '12px' }}>{l.factura_antiga || '—'} → <strong>{l.factura_nova}</strong></td>
                      <td style={tdBase}>
                        <div>{l.produto_nome}</div>
                        <div style={{ fontSize: '10px', color: C.textMuted, fontFamily: 'monospace' }}>{l.produto_ean || '-'}</div>
                      </td>
                      <td style={{ ...tdBase, fontSize: '11px', color: C.textMuted }}>{l.cliente_email}</td>
                      <td style={tdBase}>
                        <span style={{ display: 'inline-block', padding: '1px 6px', background: md.bg, color: md.fg, borderRadius: '8px', fontSize: '9px', fontWeight: '700' }}>{md.txt}</span>
                      </td>
                      <td style={tdBase}>
                        <span style={{ display: 'inline-block', padding: '2px 8px', background: mb.bg, color: mb.fg, borderRadius: '10px', fontSize: '10px', fontWeight: '700' }}>{mb.txt}</span>
                      </td>
                      <td style={{ ...tdBase, textAlign: 'right', fontFamily: 'monospace', fontSize: '12px' }}>
                        {l.preco_custo_antigo != null ? fmt(l.preco_custo_antigo) + ' €' : '—'} → <strong>{fmt(l.preco_custo_novo)} €</strong>
                      </td>
                      <td style={{ ...tdBase, textAlign: 'right', fontFamily: 'monospace', fontWeight: '700', color: corDelta }}>
                        {l.delta_custo != null ? (l.delta_custo > 0 ? '+' : '') + fmt(l.delta_custo) + ' €' : '—'}
                      </td>
                      <td style={{ ...tdBase, textAlign: 'right', fontFamily: 'monospace', fontSize: '12px' }}>
                        {l.preco_venda_antigo != null ? fmt(l.preco_venda_antigo) + ' €' : '—'} → {l.preco_venda_novo != null ? <strong style={{ color: '#107c10' }}>{fmt(l.preco_venda_novo)} €</strong> : <em style={{ color: C.textMuted }}>não actualizado</em>}
                      </td>
                      <td style={{ ...tdBase, textAlign: 'right', fontFamily: 'monospace', fontWeight: '700', color: corDeltaVenda }}>
                        {l.delta_venda != null ? (l.delta_venda > 0 ? '+' : '') + fmt(l.delta_venda) + ' €' : '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        <p style={{ fontSize: '11px', color: C.textMuted, marginTop: '10px' }}>
          <strong>Legenda:</strong> <strong style={{ color: '#a4262c' }}>Subida</strong> = custo aumentou (venda também sobe se AUTO). <strong style={{ color: '#107c10' }}>Descida</strong> = custo baixou (venda mantida). <strong style={{ color: '#0078d4' }}>Primeira vez</strong> = custo inicializado. <strong style={{ color: '#c07400' }}>Manual</strong> = aviso, preço venda não foi tocado (admin ajusta manualmente se quiser).
        </p>
      </div>
    </div>
  )
}
